import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import {
  normalizeManualLocale,
  persistManualLocale,
  readStoredManualLocale,
  type ManualLocale,
} from "./locale";

type LocaleContextValue = {
  locale: ManualLocale;
  setLocale: (locale: ManualLocale) => void;
};

const LocaleContext = createContext<LocaleContextValue>({
  locale: "en",
  setLocale: () => {},
});

export function ManualLocaleProvider({ children }: { children: React.ReactNode }) {
  const { user, saveLanguage } = useAuth() as {
    user: { preferredLanguage?: string } | null;
    saveLanguage: (language: ManualLocale) => void;
  };
  const [locale, setLocaleState] = useState<ManualLocale>(
    () => readStoredManualLocale() || "en"
  );

  useEffect(() => {
    if (readStoredManualLocale()) return;
    const fromAccount = normalizeManualLocale(user?.preferredLanguage);
    if (fromAccount) setLocaleState(fromAccount);
  }, [user?.preferredLanguage]);

  useEffect(() => {
    const previous = document.documentElement.lang;
    document.documentElement.lang = locale;
    return () => {
      document.documentElement.lang = previous || "en";
    };
  }, [locale]);

  const setLocale = useCallback(
    (next: ManualLocale) => {
      setLocaleState(next);
      persistManualLocale(next);
      saveLanguage(next);
    },
    [saveLanguage]
  );

  const value = useMemo(() => ({ locale, setLocale }), [locale, setLocale]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useManualLocale() {
  return useContext(LocaleContext);
}
